import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, type FeedbackImageInput, api } from "../lib/api";
import { AdminAlert } from "./AdminAlert";

const ACCEPTED_MIME = ["image/png", "image/jpeg", "image/webp"] as const;
type AcceptedMime = (typeof ACCEPTED_MIME)[number];

function isAcceptedMime(mime: string): mime is AcceptedMime {
  return (ACCEPTED_MIME as readonly string[]).includes(mime);
}

/** Liest eine Bilddatei in das Feedback-Payload-Format (raw base64 ohne data-URL-Prefix). */
function fileToImage(file: File): Promise<FeedbackImageInput | null> {
  if (!isAcceptedMime(file.type)) return Promise.resolve(null);
  const mime = file.type;
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const base64 = result.split(",")[1] ?? "";
      resolve(base64 ? { filename: file.name || "screenshot", mime, data_base64: base64 } : null);
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/**
 * Feedback-Button für den Hub selbst (#135), oben links neben der Hub-Waffle.
 * Öffnet ein Modal (Text + optionaler Screenshot via Datei-Auswahl oder Einfügen)
 * und legt ein Issue im Hub-Repo an — gleiche Mechanik wie das App-Feedback der
 * Waffle, nur gegen das fest konfigurierte Ziel (`feedback.hub_source_url`).
 */
export function HubFeedbackButton(): JSX.Element {
  const { t } = useTranslation();
  const title = t("hubFeedback.title");
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [image, setImage] = useState<FeedbackImageInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issueUrl, setIssueUrl] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !busy) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy]);

  // Dismiss (Overlay-Klick, Escape, Abbrechen) verwirft nur transiente UI —
  // der Entwurf (message + image) bleibt erhalten und wird beim erneuten Öffnen
  // wieder gefüllt. Angeglichen an das App-Feedback-Embed (waffle.js), das den
  // Text ebenfalls nur nach erfolgreichem Absenden leert (mindcode #5).
  const close = (): void => {
    setOpen(false);
    setError(null);
    setIssueUrl(null);
  };

  const pickFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    const img = await fileToImage(file);
    if (img) setImage(img);
    else setError(t("hubFeedback.imageRejected"));
  };

  const onPaste = async (e: React.ClipboardEvent): Promise<void> => {
    const file = Array.from(e.clipboardData.files).find((f) => isAcceptedMime(f.type));
    if (file) {
      e.preventDefault();
      await pickFile(file);
    }
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.submitHubFeedback({
        message,
        ...(image ? { image } : {}),
        context: {
          url: window.location.href,
          user_agent: navigator.userAgent,
          logs: [],
        },
      });
      setIssueUrl(result.issue_url);
      // Erst nach erfolgreichem Submit den Entwurf leeren, damit das nächste
      // Öffnen ein leeres Formular zeigt (mindcode #5).
      setMessage("");
      setImage(null);
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="hub-waffle" title={title} onClick={() => setOpen(true)}>
        <span className="sr-only">{title}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8l-4 4V5a1 1 0 0 1 1-1Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open ? (
        // biome-ignore lint/a11y/useKeyWithClickEvents: Backdrop ist optional; Escape (oben) deckt die Tastatur ab.
        <div
          className="modal__overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) close();
          }}
        >
          {/* biome-ignore lint/a11y/useSemanticElements: natives <dialog> bräuchte showModal(), das in jsdom (Komponententests) nicht implementiert ist. */}
          <div className="modal admin__form" role="dialog" aria-modal="true" aria-label={title}>
            <h3>{title}</h3>
            {issueUrl ? (
              <>
                <AdminAlert kind="success">{t("hubFeedback.success")}</AdminAlert>
                <p>
                  <a href={issueUrl} target="_blank" rel="noreferrer">
                    {t("hubFeedback.openIssue")}
                  </a>
                </p>
                <div className="admin__form-actions">
                  <button type="button" className="primary" onClick={close}>
                    {t("hubFeedback.close")}
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={(e) => void submit(e)}>
                <p className="launchpad__hint">{t("hubFeedback.body")}</p>
                {error ? <AdminAlert kind="error">{error}</AdminAlert> : null}
                <div className="admin__field">
                  <label htmlFor="hub-feedback-message">{t("hubFeedback.message")}</label>
                  <textarea
                    id="hub-feedback-message"
                    ref={textareaRef}
                    rows={5}
                    required
                    minLength={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onPaste={(e) => void onPaste(e)}
                  />
                </div>
                <div className="admin__field">
                  <label htmlFor="hub-feedback-image">{t("hubFeedback.image")}</label>
                  <input
                    id="hub-feedback-image"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => void pickFile(e.target.files?.[0])}
                  />
                  <p className="launchpad__hint">
                    {image
                      ? t("hubFeedback.imageAttached", { name: image.filename })
                      : t("hubFeedback.imageHint")}
                  </p>
                </div>
                <div className="admin__form-actions">
                  <button type="submit" className="primary" disabled={busy}>
                    {busy ? t("hubFeedback.sending") : t("hubFeedback.send")}
                  </button>
                  <button type="button" onClick={close} disabled={busy}>
                    {t("hubFeedback.cancel")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
